/**
 * MCP 错误处理模块
 * 
 * 提供统一的错误类型、错误码和错误响应格式
 * 
 * @module core/errors
 */

/**
 * 错误码枚举
 */
export enum ErrorCode {
    // 通用错误 1xxx
    UNKNOWN = 1000,
    INVALID_PARAMETER = 1001,
    NOT_FOUND = 1002,
    PERMISSION_DENIED = 1003,
    
    // 项目分析错误 2xxx
    PROJECT_NOT_FOUND = 2001,
    PROJECT_PARSE_ERROR = 2002,
    CONFIG_READ_ERROR = 2003,
    
    // 规范相关错误 3xxx
    STANDARD_NOT_FOUND = 3001,
    STANDARD_LOAD_ERROR = 3002,
    PRESET_NOT_FOUND = 3003,
    
    // 模板相关错误 4xxx
    TEMPLATE_NOT_FOUND = 4001,
    TEMPLATE_PARSE_ERROR = 4002,
    
    // Agent 相关错误 5xxx
    AGENT_NOT_FOUND = 5001,
    AGENT_LOAD_ERROR = 5002,
    
    // 配置生成错误 6xxx
    CONFIG_GENERATE_ERROR = 6001,
    FILE_WRITE_ERROR = 6002,
}

/**
 * 错误码到消息的映射
 */
const ERROR_MESSAGES: Record<ErrorCode, string> = {
    [ErrorCode.UNKNOWN]: '未知错误',
    [ErrorCode.INVALID_PARAMETER]: '参数无效',
    [ErrorCode.NOT_FOUND]: '资源未找到',
    [ErrorCode.PERMISSION_DENIED]: '权限不足',
    
    [ErrorCode.PROJECT_NOT_FOUND]: '项目目录不存在',
    [ErrorCode.PROJECT_PARSE_ERROR]: '项目解析失败',
    [ErrorCode.CONFIG_READ_ERROR]: '配置文件读取失败',
    
    [ErrorCode.STANDARD_NOT_FOUND]: '规范未找到',
    [ErrorCode.STANDARD_LOAD_ERROR]: '规范加载失败',
    [ErrorCode.PRESET_NOT_FOUND]: '预设未找到',
    
    [ErrorCode.TEMPLATE_NOT_FOUND]: '模板未找到',
    [ErrorCode.TEMPLATE_PARSE_ERROR]: '模板解析失败',
    
    [ErrorCode.AGENT_NOT_FOUND]: 'Agent 未找到',
    [ErrorCode.AGENT_LOAD_ERROR]: 'Agent 加载失败',
    
    [ErrorCode.CONFIG_GENERATE_ERROR]: '配置生成失败',
    [ErrorCode.FILE_WRITE_ERROR]: '文件写入失败',
};

/**
 * MCP 统一错误类
 */
export class MCPError extends Error {
    /** 错误码 */
    readonly code: ErrorCode;
    /** 原始错误 */
    readonly cause?: Error;
    /** 错误上下文 */
    readonly context?: Record<string, unknown>;
    
    constructor(
        code: ErrorCode,
        message?: string,
        options?: {
            cause?: Error;
            context?: Record<string, unknown>;
        }
    ) {
        const defaultMessage = ERROR_MESSAGES[code] || '未知错误';
        super(message || defaultMessage);
        
        this.name = 'MCPError';
        this.code = code;
        this.cause = options?.cause;
        this.context = options?.context;
        
        // 保持正确的原型链
        Object.setPrototypeOf(this, MCPError.prototype);
    }
    
    /**
     * 转换为 JSON 格式（用于 MCP 响应）
     */
    toJSON(): Record<string, unknown> {
        return {
            error: true,
            code: this.code,
            message: this.message,
            ...(this.context && { context: this.context }),
        };
    }
    
    /**
     * 转换为 MCP 响应格式
     */
    toResponse(): { content: Array<{ type: string; text: string }> } {
        return {
            content: [{
                type: 'text',
                text: JSON.stringify(this.toJSON(), null, 2)
            }]
        };
    }
}

/**
 * 便捷错误创建函数
 */
export const Errors = {
    invalidParam: (param: string, reason?: string) => 
        new MCPError(ErrorCode.INVALID_PARAMETER, 
            `参数 "${param}" 无效${reason ? `: ${reason}` : ''}`,
            { context: { param } }
        ),
    
    notFound: (resource: string, id?: string) =>
        new MCPError(ErrorCode.NOT_FOUND,
            `${resource}${id ? ` "${id}"` : ''} 未找到`,
            { context: { resource, id } }
        ),
    
    projectNotFound: (path: string) =>
        new MCPError(ErrorCode.PROJECT_NOT_FOUND,
            `项目目录不存在: ${path}`,
            { context: { path } }
        ),
    
    standardNotFound: (id: string) =>
        new MCPError(ErrorCode.STANDARD_NOT_FOUND,
            `规范 "${id}" 未找到`,
            { context: { standardId: id } }
        ),
    
    presetNotFound: (preset: string) =>
        new MCPError(ErrorCode.PRESET_NOT_FOUND,
            `预设 "${preset}" 未找到`,
            { context: { preset } }
        ),
    
    templateNotFound: (id: string) =>
        new MCPError(ErrorCode.TEMPLATE_NOT_FOUND,
            `模板 "${id}" 未找到`,
            { context: { templateId: id } }
        ),
    
    agentNotFound: (id: string) =>
        new MCPError(ErrorCode.AGENT_NOT_FOUND,
            `Agent "${id}" 未找到`,
            { context: { agentId: id } }
        ),
    
    fileWriteError: (path: string, cause?: Error) =>
        new MCPError(ErrorCode.FILE_WRITE_ERROR,
            `文件写入失败: ${path}`,
            { cause, context: { path } }
        ),
    
    wrap: (error: unknown, code: ErrorCode = ErrorCode.UNKNOWN) => {
        if (error instanceof MCPError) return error;
        
        const message = error instanceof Error ? error.message : String(error);
        return new MCPError(code, message, {
            cause: error instanceof Error ? error : undefined
        });
    }
};

/**
 * 安全执行函数，捕获错误并转换为 MCPError
 */
export async function safeExecute<T>(
    fn: () => Promise<T>,
    errorCode: ErrorCode = ErrorCode.UNKNOWN
): Promise<T> {
    try {
        return await fn();
    } catch (error) {
        throw Errors.wrap(error, errorCode);
    }
}

/**
 * 创建标准的错误响应
 */
export function errorResponse(error: unknown): { content: Array<{ type: string; text: string }> } {
    if (error instanceof MCPError) {
        return error.toResponse();
    }
    
    const message = error instanceof Error ? error.message : String(error);
    return {
        content: [{
            type: 'text',
            text: JSON.stringify({
                error: true,
                code: ErrorCode.UNKNOWN,
                message
            }, null, 2)
        }]
    };
}
